/**
 * Utility to convert legacy CSV backups to the new JSON format
 * 
 * This script reads the old CSV format and converts it to the new JSON structure
 * that is compatible with the enhanced backup/restore system.
 */

const csvToJson = (csvString) => {
  const lines = csvString.split('\n');
  const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
  const result = [];

  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    
    // Handle quoted fields properly
    const currentLine = lines[i].match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g) || [];
    const obj = {};
    
    for (let j = 0; j < headers.length; j++) {
      let value = currentLine[j] ? currentLine[j].trim().replace(/^"|"$/g, '') : '';
      
      // Convert specific fields to appropriate types
      if (headers[j] === 'id') {
        obj[headers[j]] = value ? parseInt(value, 10) : null;
      } else if (headers[j] === 'percentage_achieved') {
        obj[headers[j]] = value ? parseInt(value, 10) : 0;
      } else if (headers[j] === 'is_archived') {
        obj[headers[j]] = value === 'true';
      } else {
        obj[headers[j]] = value;
      }
    }
    result.push(obj);
  }
  
  return result;
};

export const convertCsvBackupToJson = (csvContent) => {
  // Split CSV content by table markers
  const tables = csvContent.split('---TABLE:');
  
  // Initialize the JSON structure
  const jsonBackup = {
    meta: {
      backup_date: new Date().toISOString(),
      backup_version: '3.0-json',
      converted_from_csv: true
    },
    members: [],
    notes: [],
    coaches: [],
    memberGoals: [],
    memberMiniGoals: [],
    appSettings: []
  };

  // Process each table
  for (const table of tables) {
    if (!table.trim()) continue;
    
    const tableNameMatch = table.match(/^(\w+)/);
    if (!tableNameMatch) continue;
    
    const tableName = tableNameMatch[1];
    const csvData = table.replace(/^[^\n]*\n/, ''); // Remove table name line
    
    // Map table names to their JSON counterparts
    const tableMapping = {
      members_bpa_5e7gf: 'members',
      notes_bpa_5e7gf: 'notes',
      coaches_bpa_5e7gf: 'coaches',
      member_goals_bpa_5e7gf: 'memberGoals',
      member_mini_goals_bpa_5e7gf: 'memberMiniGoals',
      app_settings_bpa_5e7gf: 'appSettings'
    };
    
    const jsonTableName = tableMapping[tableName];
    if (jsonTableName) {
      jsonBackup[jsonTableName] = csvToJson(csvData);
    }
  }
  
  return jsonBackup;
};

// Browser-based file conversion utility
export const convertCsvFileToJson = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const csvContent = event.target.result;
        const jsonBackup = convertCsvBackupToJson(csvContent);
        resolve(jsonBackup);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
    
    reader.readAsText(file);
  });
};

// Create downloadable JSON file
export const downloadJsonBackup = (jsonBackup, filename = `converted_backup_${new Date().toISOString().split('T')[0]}.json`) => {
  const blob = new Blob([JSON.stringify(jsonBackup, null, 2)], {
    type: 'application/json;charset=utf-8'
  });
  
  // Create download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};